import React, { useEffect, useState } from 'react';
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  ChartTypes,
  PageLoader,
  SearchableDropdown,
} from '@scholastic/volume-react';

import { HeaderLinks } from './HeaderLinks/HeaderLinks';
import ReportChart from './ReportChart/ReportChart';
import ReportTable from './ReportTable/ReportTable';
import { TransformChartDataResponse } from '../ReportDataTransform';

import './ReportChartBody.scss';
import { SearchableDropdownValue } from '../../Home/Home.dataparser';

export interface ReportChartBodyProps {
  /**
   * It would holds the user name
   */
  loggedInUserName: string;

  /**
   * It will hold the product name
   */
  productName: string;

  /**
   * It will hold the report type
   */
  reportType: string;

  /**
   * It will hold the chart data
   */
  chartData: TransformChartDataResponse | null;

  /**
   * It will hold the previous 6 month click function
   */
  onPreviousClick: () => void;

  /**
   * It will hold the next 6 month click function
   */
  onNextClick: () => void;

  /**
   * It will hold the pagination function
   */
  loadMore: (pageNo: number) => void;

  /**
   * It holds the current page number
   */
  page: number;

  /**
   * It will hold the total pages number to display
   */
  totalPages: number;

  /**
   * This holds the default selcted Tab Name
   */
  selectedTab: (tabName: string) => void;

  /**
   * This will hold the maximum groups to show per page
   */
  maxPageSize: number;

  /**
   * It will hold whether the data is loaded from api
   * @default :false
   */
  dataLoaded?: boolean;
}

const ReportChartBody = ({
  loggedInUserName,
  productName,
  reportType,
  chartData,
  onPreviousClick,
  onNextClick,
  loadMore,
  page,
  totalPages,
  dataLoaded = false,
  maxPageSize,
  selectedTab,
}: ReportChartBodyProps) => {
  const [activeTab, setActiveTab] = useState('1');
  const [totalTabChartType, setTotalTabChartType] = useState<ChartTypes>('bar');
  const [graphData, setGraphData] = useState<TransformChartDataResponse | null>(null);

  const isGroupReports =
    reportType === 'documentUsageByGroup' || reportType === 'sessionUsageByGroup';
  const groupArray: Array<SearchableDropdownValue> = [];
  for (let i = 0; i < totalPages; i++) {
    const labelText = `Showing page ${i + 1}`;
    groupArray.push({ label: labelText, value: (i + 1).toString() });
  }

  useEffect(() => {
    setGraphData(chartData);
  }, [chartData]);

  useEffect(() => {
    if (activeTab === '1') {
      selectedTab('Total Usage');
    } else if (activeTab === '2') {
      selectedTab('Remote Usage');
    } else if (activeTab === '3') {
      selectedTab('Onsite Usage');
    } else {
      selectedTab('Help');
    }

    // eslint-disable-next-line
  }, [activeTab]);

  const toggleChartType = () => {
    if (totalTabChartType === 'bar') {
      setTotalTabChartType('line');
    } else {
      setTotalTabChartType('bar');
    }
  };

  const showReportType = (reportType: string) => {
    if (reportType === 'usageSummary') {
      return 'Overall Usage Summary';
    } else if (reportType === 'documentUsage') {
      return 'Detailed Document Usage';
    } else if (reportType === 'sessionUsage') {
      return 'Detailed Session Usage';
    } else if (reportType === 'documentUsageByGroup') {
      return 'Document Usage By Group';
    } else {
      return 'Session Usage By Group';
    }
  };

  const paginateGroups = (item: SearchableDropdownValue) => {
    loadMore(parseInt(item.value) - 1);
  };

  return (
    <div className="stats-chart-body section-to-print">
      <h2 className="stats-chart-body__title m-1">
        <span>{loggedInUserName}</span>
        <span> - {productName}</span>
      </h2>
      <h4 className="stats-chart-body__type m-1">{showReportType(reportType)}</h4>
      {!dataLoaded && <PageLoader color={'dark'} size={'large'} type={'border'} />}
      {isGroupReports && totalPages > 1 && (
        <div className="stats-chart-body__pagination">
          {' '}
          <SearchableDropdown
            items={groupArray}
            placeholder="Select Groups"
            onItemSelect={paginateGroups}
            defaultValue={groupArray[page].value}
          />
        </div>
      )}
      <div className={'stats-chart-body__tabs ' + (!dataLoaded && 'stats-chart-body__tabs--blur')}>
        <Nav tabs>
          <NavItem active={activeTab === '1'}>
            <NavLink
              onClick={() => {
                setActiveTab('1');
              }}
              id="total-usage"
            >
              Total Usage
            </NavLink>
          </NavItem>
          <NavItem active={activeTab === '2'}>
            <NavLink
              onClick={() => {
                setActiveTab('2');
              }}
              id="remote-usage"
            >
              Remote Usage
            </NavLink>
          </NavItem>
          <NavItem active={activeTab === '3'}>
            <NavLink
              onClick={() => {
                setActiveTab('3');
              }}
              id="onsite-usage"
            >
              Onsite Usage
            </NavLink>
          </NavItem>
          {/*<NavItem active={activeTab === '4'}>
            <NavLink
              onClick={() => {
                setActiveTab('4');
              }}
            >
              Individual Usage
            </NavLink>
          </NavItem>*/}
          <NavItem active={activeTab === '5'}>
            <NavLink
              onClick={() => {
                setActiveTab('5');
              }}
              id="help-tab"
            >
              Help With Stats
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTabId={activeTab}>
          <TabPane tabId="1">
            <HeaderLinks
              onPreviousClick={onPreviousClick}
              toggleChartType={toggleChartType}
              onNextClick={onNextClick}
            />

            <ReportChart graphData={graphData?.totalChartobj} chartType={totalTabChartType} />
          </TabPane>
          <TabPane tabId="2">
            <HeaderLinks
              onPreviousClick={onPreviousClick}
              toggleChartType={toggleChartType}
              onNextClick={onNextClick}
            />

            <ReportChart graphData={graphData?.remoteChartObj} chartType={totalTabChartType} />
          </TabPane>
          <TabPane tabId="3">
            <HeaderLinks
              onPreviousClick={onPreviousClick}
              toggleChartType={toggleChartType}
              onNextClick={onNextClick}
            />

            <ReportChart graphData={graphData?.onsiteChartObj} chartType={totalTabChartType} />
          </TabPane>
          {/*<TabPane tabId="4">
            <HeaderLinks
              onPreviousClick={onPreviousClick}
              toggleChartType={toggleChartType}
              onNextClick={onNextClick}
            />

            <ReportChart graphData={graphData?.individualChartObj} chartType={totalTabChartType} />
          </TabPane>*/}
          <TabPane tabId="5" className="stats-chart-body__help-container">
            <h4 className="stats-chart-body__sub-title">Document usage:</h4>

            <p className="stats-chart-body__description">
              Each page viewed is counted as a document. This includes encyclopedia articles, maps,
              videos, as well as other browse or index pages.
            </p>

            <h4 className="stats-chart-body__sub-title">Session:</h4>

            <p className="stats-chart-body__description">
              Session is an estimated calculation of the number of individual users accessing our
              site. Sessions are determined based on the Counter Compliance guidelines: a session
              statistic is logged the first time that a user accesses our site. Thereafter a new
              session is logged either when a logout occurs, the browser window is closed, or when
              the browser has been inactive for a period of five minutes or more.
            </p>
          </TabPane>
        </TabContent>
        {productName === 'BookFlix' && reportType === 'usageSummary' && (
          <p className="stats-chart-body__description stats-chart-body__description--notice ">
            *Sessions - Due to a programming change in how stats are recorded that accompanied the
            release of BookFlix 2.0, session activity from August 1, 2018 to July 31, 2019 was not
            captured per our standard guidelines. This has been corrected for all reports moving
            forward. In an effort to reflect more accurate usage statistics from August 1, 2018 to
            July 31, 2019, we have applied the standard guidelines to the actual sessions data. The
            *sessions you will see for August 1, 2018 to July 31, 2019 are estimated based on the
            original statistical capture method prior to the release of BookFlix 2.0.
          </p>
        )}

        {activeTab === '1' && <ReportTable tableData={graphData?.totalChartobj} />}
        {activeTab === '2' && <ReportTable tableData={graphData?.remoteChartObj} />}
        {activeTab === '3' && <ReportTable tableData={graphData?.onsiteChartObj} />}
      </div>
    </div>
  );
};

export default ReportChartBody;
